// Phase 2 §4: Extended ScenarioWorkbench with ProjectToCapTableButton
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Download,
  RefreshCw,
  Sparkles,
  Plus,
  Info,
  TrendingUp,
  DollarSign,
  Users,
  ArrowUpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { ProjectToCapTableButton } from './ProjectToCapTableButton';

interface ScenarioWorkbenchProps {
  companyId: string;
  roundId?: string;
  baseCurrency?: string;
}

interface SafeEntry {
  name: string;
  amount: string;
  cap: string;
  discount: string;
}

interface PrefEntry {
  class: string;
  multiple: string;
  participating: string;
}

interface ScenarioResult {
  name: string;
  type: string;
  data: any;
  scenarioId?: string;
}

export function ScenarioWorkbench({
  companyId,
  roundId,
  baseCurrency = 'USD',
}: ScenarioWorkbenchProps) {
  const [cap, setCap] = useState<any[]>([]);
  const [round, setRound] = useState<any | null>(null);
  const [tab, setTab] = useState('dilution');
  const [busy, setBusy] = useState(false);
  const [currency, setCurrency] = useState(baseCurrency);

  // scenario state with smart defaults
  const [dilution, setDilution] = useState({
    raise: '2000000',
    preMoney: '5000000',
    poolPct: '10',
    instrument: 'EQUITY',
  });
  const [safes, setSafes] = useState<SafeEntry[]>([
    { name: 'Investor A', amount: '250000', cap: '6000000', discount: '20' },
  ]);
  const [nextRound, setNextRound] = useState({ preMoney: '6000000', raise: '1500000' });
  const [poolTarget, setPoolTarget] = useState('10');
  const [exit, setExit] = useState({ value: '25000000' });
  const [prefs, setPrefs] = useState<PrefEntry[]>([
    { class: 'Series A Pref', multiple: '1.0', participating: 'NON_PARTICIPATING' },
  ]);

  const [results, setResults] = useState<ScenarioResult[]>([]);

  async function load() {
    try {
      setBusy(true);
      const [{ data: ct }, { data: rd }] = await Promise.all([
        supabase.rpc('sm_load_cap_table', { company_id: companyId }),
        roundId
          ? supabase.rpc('sm_load_funding_round', { round_id: roundId })
          : Promise.resolve({ data: null }),
      ]);
      setCap(ct || []);
      setRound(rd || null);

      // prefill from round if given
      if (rd) {
        setDilution({
          raise: String(rd.target_amount || '2000000'),
          preMoney: String(rd.pre_money || '5000000'),
          instrument: rd.instrument || 'EQUITY',
          poolPct: String((rd.option_pool_increase || 0.1) * 100),
        });
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
  }, [companyId, roundId]);

  async function addScenario(name: string, type: string, data: any) {
    setResults((r) => [...r.filter((x) => x.name !== name), { name, type, data }]);

    // Save scenario snapshot to database
    try {
      // Build cap table snapshot from simulation data
      const capTableSnapshot = buildCapTableSnapshot(data, type);

      const { data: snapshot, error } = await supabase
        .from('cap_table_snapshots')
        .insert([
          {
            company_id: companyId,
            label: name,
            reason: `Scenario: ${type}`,
            created_by: (await supabase.auth.getUser()).data.user?.id || '',
            snapshot: capTableSnapshot,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      toast.success(`Scenario "${name}" saved successfully`);

      // Update results with scenario_id
      setResults((r) => r.map((s) => (s.name === name ? { ...s, scenarioId: snapshot.id } : s)));
    } catch (e: any) {
      toast.error(`Scenario saved but snapshot failed: ${e.message}`);
    }
  }

  function buildCapTableSnapshot(data: any, type: string): any[] {
    // Convert simulation results to standardized cap table format
    if (!data) return [];

    const snapshot: any[] = [];

    if (type === 'DILUTION' && data.post_money_fd) {
      snapshot.push({
        holder: 'Founders',
        share_class: 'Ordinary',
        shares: (data.post_money_fd || 0) * (data.owner_pct || 0),
        pct: (data.owner_pct || 0) * 100,
      });

      if (data.esop_pct) {
        snapshot.push({
          holder: 'Option Pool',
          share_class: 'Ordinary',
          shares: (data.post_money_fd || 0) * (data.esop_pct || 0),
          pct: (data.esop_pct || 0) * 100,
        });
      }

      if (data.new_money_pct) {
        snapshot.push({
          holder: 'New Investors',
          share_class: 'Preferred',
          shares: (data.post_money_fd || 0) * (data.new_money_pct || 0),
          pct: (data.new_money_pct || 0) * 100,
        });
      }
    } else if (type === 'SAFE' && data.converted_cap_table) {
      // Use the converted cap table from SAFE simulation
      return data.converted_cap_table.map((row: any) => ({
        holder: row.holder || row.name,
        share_class: row.share_class || 'Ordinary',
        shares: row.shares || 0,
        pct: row.pct || 0,
      }));
    } else if (type === 'POOL' && data.new_cap_table) {
      // Use the new cap table from pool refresh
      return data.new_cap_table.map((row: any) => ({
        holder: row.holder || row.name,
        share_class: row.share_class || 'Ordinary',
        shares: row.shares || 0,
        pct: row.pct || 0,
      }));
    } else if (type === 'EXIT' && data.waterfall) {
      // Convert exit waterfall to cap table format
      return data.waterfall.map((row: any) => ({
        holder: row.holder || row.class,
        share_class: row.share_class || 'Ordinary',
        shares: row.shares || 0,
        pct: row.pct || 0,
      }));
    }

    return snapshot;
  }

  async function runDilution() {
    if (
      !dilution.raise ||
      !dilution.preMoney ||
      Number(dilution.raise) <= 0 ||
      Number(dilution.preMoney) <= 0
    ) {
      toast.error('Please enter valid positive values for raise amount and pre-money valuation');
      return;
    }

    try {
      setBusy(true);
      const { data, error } = await supabase.functions.invoke('sm-dilution-sim', {
        body: {
          companyId,
          round: {
            raise: Number(dilution.raise || 0),
            pre_money: Number(dilution.preMoney || 0),
            instrument: dilution.instrument,
            option_pool_increase: Number(dilution.poolPct || 0) / 100,
          },
          baseCurrency: currency,
        },
      });
      if (error) throw error;
      addScenario(
        `Dilution @ ${num(dilution.preMoney)} pre / ${num(dilution.raise)} raise`,
        'DILUTION',
        data
      );
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function runSafes() {
    if (safes.length === 0 || safes.some((s) => !s.amount || Number(s.amount) <= 0)) {
      toast.error('Please add at least one SAFE with valid amounts');
      return;
    }

    try {
      setBusy(true);
      const payload = safes.map((s) => ({
        name: s.name,
        amount: Number(s.amount || 0),
        cap: Number(s.cap || 0),
        discount: Number(s.discount || 0) / 100,
      }));
      const { data, error } = await supabase.functions.invoke('sm-safe-convert-sim', {
        body: {
          companyId,
          safes: payload,
          nextRound: {
            pre_money: Number(nextRound.preMoney || 0),
            raise: Number(nextRound.raise || 0),
          },
          baseCurrency: currency,
        },
      });
      if (error) throw error;
      addScenario(`SAFE conversion @ next pre=${num(nextRound.preMoney)}`, 'SAFE', data);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function runPool() {
    if (!poolTarget || Number(poolTarget) <= 0 || Number(poolTarget) > 100) {
      toast.error('Please enter a valid pool percentage between 0 and 100');
      return;
    }

    try {
      setBusy(true);
      const { data, error } = await supabase.functions.invoke('sm-pool-refresh-sim', {
        body: {
          companyId,
          targetPostPct: Number(poolTarget || 0) / 100,
          baseCurrency: currency,
        },
      });
      if (error) throw error;
      addScenario(`Pool refresh to ${poolTarget}%`, 'POOL', data);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function runExit() {
    if (!exit.value || Number(exit.value) <= 0) {
      toast.error('Please enter a valid exit valuation');
      return;
    }

    try {
      setBusy(true);
      const stack = prefs.map((p) => ({
        class: p.class,
        multiple: Number(p.multiple || 1),
        participating: p.participating,
      }));
      const { data, error } = await supabase.functions.invoke('sm-exit-waterfall-sim', {
        body: {
          companyId,
          exitValue: Number(exit.value || 0),
          stack,
          baseCurrency: currency,
        },
      });
      if (error) throw error;
      // A liquidation preference is a multiple of money invested, which the
      // simulation reads from price_per_share on the ledger. Where that is
      // missing the preference is understated, and the user needs telling
      // rather than being handed a confident number.
      if (Array.isArray(data?.warnings)) {
        data.warnings.forEach((w: string) => toast.warning(w));
      }
      addScenario(`Exit ${num(exit.value)}`, 'EXIT', data);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  function patchSafe(i: number, v: SafeEntry) {
    setSafes((prev) => prev.map((s, idx) => (idx === i ? v : s)));
  }

  function patchPref(i: number, v: PrefEntry) {
    setPrefs((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  }

  return (
    <TooltipProvider>
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Scenario Workbench</CardTitle>
            <CardDescription>
              Run financial simulations to model different funding scenarios
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {results.length === 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Getting Started:</strong> Run a simulation below to create your first
                scenario. You can then compare different scenarios and reconcile them against your
                actual cap table.
              </AlertDescription>
            </Alert>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-3 grid w-full grid-cols-4">
              <TabsTrigger value="dilution">
                <TrendingUp className="h-4 w-4 mr-2" />
                Dilution
              </TabsTrigger>
              <TabsTrigger value="safe">
                <DollarSign className="h-4 w-4 mr-2" />
                SAFEs
              </TabsTrigger>
              <TabsTrigger value="pool">
                <Users className="h-4 w-4 mr-2" />
                Option Pool
              </TabsTrigger>
              <TabsTrigger value="exit">
                <ArrowUpCircle className="h-4 w-4 mr-2" />
                Exit
              </TabsTrigger>
            </TabsList>

            {/* Dilution */}
            <TabsContent value="dilution">
              <div className="grid sm:grid-cols-4 gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Raise Amount</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Total capital being raised in this funding round</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 2,000,000"
                    value={dilution.raise}
                    onChange={(e) => setDilution({ ...dilution, raise: e.target.value })}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Pre-Money</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Company valuation before the new investment</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 5,000,000"
                    value={dilution.preMoney}
                    onChange={(e) => setDilution({ ...dilution, preMoney: e.target.value })}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Option Pool %</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Percentage allocated for employee stock options</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 10"
                    value={dilution.poolPct}
                    onChange={(e) => setDilution({ ...dilution, poolPct: e.target.value })}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Instrument</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Type of security being issued</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select
                    value={dilution.instrument}
                    onValueChange={(v) => setDilution({ ...dilution, instrument: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EQUITY">Equity</SelectItem>
                      <SelectItem value="SAFE">SAFE</SelectItem>
                      <SelectItem value="CONVERTIBLE">Convertible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Button
                  onClick={runDilution}
                  disabled={busy || !dilution.raise || !dilution.preMoney}
                >
                  <Sparkles className="h-4 w-4 mr-1" />{' '}
                  {busy ? 'Simulating...' : 'Simulate Dilution'}
                </Button>
              </div>
            </TabsContent>

            {/* SAFE */}
            <TabsContent value="safe">
              <div className="grid sm:grid-cols-2 gap-2 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Next Round Pre-Money</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Pre-money valuation of the next funding round when SAFEs will convert</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 6,000,000"
                    value={nextRound.preMoney}
                    onChange={(e) => setNextRound({ ...nextRound, preMoney: e.target.value })}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Next Round Raise</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Amount being raised in the next round</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 1,500,000"
                    value={nextRound.raise}
                    onChange={(e) => setNextRound({ ...nextRound, raise: e.target.value })}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-4 gap-2 mb-1 text-xs text-muted-foreground font-medium px-1">
                <div>Investor Name</div>
                <div>SAFE Amount</div>
                <div>Valuation Cap</div>
                <div>Discount %</div>
              </div>
              <div className="space-y-2">
                {safes.map((s, i) => (
                  <div key={i} className="grid sm:grid-cols-4 gap-2">
                    <Input
                      placeholder="Investor"
                      value={s.name}
                      onChange={(e) => patchSafe(i, { ...s, name: e.target.value })}
                    />
                    <Input
                      placeholder="Amount"
                      value={s.amount}
                      onChange={(e) => patchSafe(i, { ...s, amount: e.target.value })}
                      className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                    />
                    <Input
                      placeholder="Valuation Cap"
                      value={s.cap}
                      onChange={(e) => patchSafe(i, { ...s, cap: e.target.value })}
                      className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                    />
                    <Input
                      placeholder="Discount %"
                      value={s.discount}
                      onChange={(e) => patchSafe(i, { ...s, discount: e.target.value })}
                      className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                    />
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSafes([...safes, { name: '', amount: '', cap: '', discount: '' }])
                  }
                >
                  <Plus className="h-4 w-4 mr-1" /> Add SAFE
                </Button>
              </div>
              <div className="mt-3">
                <Button onClick={runSafes} disabled={busy || safes.length === 0}>
                  <Sparkles className="h-4 w-4 mr-1" />{' '}
                  {busy ? 'Simulating...' : 'Simulate SAFE Conversion'}
                </Button>
              </div>
            </TabsContent>

            {/* Option Pool */}
            <TabsContent value="pool">
              <div className="grid sm:grid-cols-3 gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Target Post-Money Pool %</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Target percentage for refreshed option pool</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 15"
                    value={poolTarget}
                    onChange={(e) => setPoolTarget(e.target.value)}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Button onClick={runPool} disabled={busy || !poolTarget}>
                  <Sparkles className="h-4 w-4 mr-1" />{' '}
                  {busy ? 'Simulating...' : 'Simulate Pool Refresh'}
                </Button>
              </div>
            </TabsContent>

            {/* Exit Waterfall */}
            <TabsContent value="exit">
              <div className="grid sm:grid-cols-3 gap-2 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-medium">Exit Valuation</label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Total exit value at acquisition or IPO</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g., 50,000,000"
                    value={exit.value}
                    onChange={(e) => setExit({ ...exit, value: e.target.value })}
                    className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                {prefs.map((p, i) => (
                  <div key={i} className="grid sm:grid-cols-3 gap-2">
                    <Input
                      placeholder="Class Name"
                      value={p.class}
                      onChange={(e) => patchPref(i, { ...p, class: e.target.value })}
                    />
                    <Input
                      placeholder="Pref Multiple"
                      value={p.multiple}
                      onChange={(e) => patchPref(i, { ...p, multiple: e.target.value })}
                      className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                    />
                    <Select
                      value={p.participating}
                      onValueChange={(v) => patchPref(i, { ...p, participating: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NON_PARTICIPATING">Non-Participating</SelectItem>
                        <SelectItem value="PARTICIPATING">Participating</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPrefs([
                      ...prefs,
                      { class: '', multiple: '1.0', participating: 'NON_PARTICIPATING' },
                    ])
                  }
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Class
                </Button>
              </div>
              <div className="mt-3">
                <Button onClick={runExit} disabled={busy || !exit.value}>
                  <Sparkles className="h-4 w-4 mr-1" />{' '}
                  {busy ? 'Simulating...' : 'Simulate Exit Waterfall'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Results – comparison table */}
          {results.length > 0 ? (
            <Card className="border border-dashed">
              <CardHeader>
                <CardTitle>Scenario Comparison</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scenario</TableHead>
                      <TableHead>Post-Money</TableHead>
                      <TableHead>Share Price</TableHead>
                      <TableHead>Founder %</TableHead>
                      <TableHead>ESOP %</TableHead>
                      <TableHead>New Investor %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((s) => (
                      <TableRow key={s.name}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{s.name}</span>
                            {s.scenarioId && (
                              <ProjectToCapTableButton
                                companyId={companyId}
                                scenarioId={s.scenarioId}
                                scenarioName={s.name}
                                scenarioData={s.data}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{num(s.data?.post_money_fd)}</TableCell>
                        <TableCell>
                          {currency} {fmt(s.data?.price_per_share)}
                        </TableCell>
                        <TableCell>{pct(s.data?.owner_pct)}</TableCell>
                        <TableCell>{pct(s.data?.esop_pct)}</TableCell>
                        <TableCell>{pct(s.data?.new_money_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No scenarios created yet. Run a simulation above to see comparisons here.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

function num(n: any) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function fmt(n: any) {
  if (n == null) return '—';
  return Number(n).toFixed(2);
}

function pct(n: any) {
  if (n == null) return '—';
  return `${(Number(n) * 100).toFixed(2)}%`;
}
